import type {
  DatePickerProps as BaseDatePickerProps,
  DateRange,
  LocaleCode} from '@ur-solutions/react-components';
import {
  DateRangePicker as BaseDateRangePicker
} from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useEffect, useRef } from 'react'
import styled, { css } from 'styled-components'
import { getLocale } from '@/utils/date-fns'

const ErrorText = styled.div`
  position: absolute;
  top: -2px;
  right: 0;
  color: ${props => props.theme.colors.red};
  font-size: 0.7rem;
  white-space: nowrap;
  transform: translateY(-100%);
`

interface StyledDateRangePickerProps extends BaseDatePickerProps {
  grayed?: boolean
  disabled?: boolean
  width?: string
}

const StyledDateRangePicker = styled(
  BaseDateRangePicker
)<StyledDateRangePickerProps>`
  width: ${props => (props.width ? props.width : '400px')};
  position: initial;
  font-size: 1rem;

  @media (max-width: 768px) {
    width: 100%;
  }

  .--date-range-picker-display:has(.--input-input:focus) .--input-input,
  .--date-range-picker-display:has(.--input-input:focus) .--input-icon-right {
    border-color: ${props => props.theme.colors.secondary};
  }

  .--date-range-picker-display {
    div:first-child .--input-input {
      border-top-left-radius: ${props => props.theme.sizes.defaultBorderRadius};
      border-bottom-left-radius: ${props =>
        props.theme.sizes.defaultBorderRadius};
      border-right: 1px solid ${props => props.theme.colors.gray9};
    }

    .--input-input {
      padding: 0 1rem;
      font-size: 1.2rem;
      border-color: ${props => props.theme.colors.gray9};
    }

    .sep {
      background-color: transparent;
      width: 0;
    }

    .--input-icon-right {
      border-color: ${props => props.theme.colors.gray9};
      font-size: 1.5rem;
      border-top-right-radius: ${props =>
        props.theme.sizes.defaultBorderRadius};
      border-bottom-right-radius: ${props =>
        props.theme.sizes.defaultBorderRadius};

      i {
        color: ${props => props.theme.colors.tertiary};
      }
    }

    ${props =>
      props.disabled &&
      css`
        input {
          color: ${props.theme.colors.gray9};
        }

        [class*='--input-icon'] i {
          color: ${props.theme.colors.gray9};
        }
      `};
  }

  .container-button {
    padding: 10px 4px;
    justify-content: flex-end;
    align-items: flex-end;
  }

  .custom-checkboxes {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 6px;
    padding: 10px 4px;
  }

  .custom-checkboxes label {
    font-size: 0.85rem;
    color: ${props => props.theme.colors.gray6};
    cursor: pointer;
    display: block;
    line-height: 1.4;
    user-select: none;

    input {
      vertical-align: middle;
      margin: 0 6px 0 0;
      width: 16px;
      height: 16px;
      border: 1px solid ${props => props.theme.colors.gray9};
      border-radius: 4px;
      appearance: none;
      cursor: pointer;
      position: relative;
      background-color: ${props => props.theme.colors.white};

      &:checked {
        background-color: ${props => props.theme.colors.secondary};
        border-color: ${props => props.theme.colors.secondary};
      }

      &:checked::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 5px;
        width: 4px;
        height: 8px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }
  }

  .custom-button {
    margin-top: auto;
    padding: 8px 16px;
    background-color: ${props => props.theme.colors.secondary};
    color: ${props => props.theme.colors.white};
    border: none;
    border-radius: ${props => props.theme.sizes.defaultBorderRadius};
    cursor: pointer;
    font-size: 0.85rem;
    font-weight: 600;
    transition: all 0.2s ease;
    width: fit-content;

    &:hover {
      opacity: 0.9;
    }

    &:active {
      transform: scale(0.98);
    }
  }

  .--date-range-picker-curtain {
    width: 100%;
    grid-template-columns: 1fr 1fr;
    row-gap: 0.75rem;
  }

  .--date-picker-curtain {
    width: auto;
  }

  .--date-picker-curtain-month-info {
    display: flex;
    justify-content: center;
    font-size: 1rem;
    align-items: center;
    padding: 8px 65px;
    margin-bottom: 8px;
    position: relative;

    .--date-picker-curtain-month-info-month,
    .--date-picker-curtain-month-info-year {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px 8px;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s ease;
      font-weight: 600;
      position: relative;
    }

    .--date-picker-curtain-month-info-month {
      margin-right: 4px;
      min-width: 40px;
    }

    .--date-picker-curtain-month-info-year {
      margin-left: 4px;
      min-width: 30px;
    }
  }
`

const Label = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  display: block;
  margin: 0 0 6px 6px;
  color: ${props => props.theme.colors.gray5};
`

interface DateRangePickerProps
  extends Omit<StyledDateRangePickerProps, 'value' | 'onChange'> {
  value: DateRange
  onChange: (range: DateRange) => void
  grayed?: boolean
  label?: string
  isDate?: boolean
  isDateStart?: boolean
  onToggleDate?: (checked: boolean, type: 'isDate' | 'isDateStart') => void
  onButtonClick?: (data: {
    value: DateRange
    isDate: boolean
    isDateStart: boolean
  }) => void
  width?: string
}

export const DateRangePickerCustom: React.FC<DateRangePickerProps> = ({
  grayed,
  background: backgroundProp,
  value,
  onChange,
  disabled,
  label,
  isDate,
  isDateStart,
  onToggleDate,
  onButtonClick,
  width,
  ...datePickerProps
}) => {
  const locale = getLocale().code as LocaleCode
  const dateRangePickerRef = useRef<HTMLDivElement>(null)
  const background = grayed ? 'quaternary' : backgroundProp ?? 'white'
  const translations = useTranslate({
    date: 'common.date',
    dateStart: 'common.date-start',
    createDate: 'common.create-date',
    invalidDate: 'common.invalid-date',
    filter: 'common.filter',
  })

  const currentValuesRef = useRef({ value, isDate, isDateStart })

  useEffect(() => {
    currentValuesRef.current = { value, isDate, isDateStart }
  }, [value, isDate, isDateStart])

  const handleChange = (dateValue: Date) => {
    onChange?.(dateValue as any)
  }

  useEffect(() => {
    if (!dateRangePickerRef.current) return

    const colorizeDisabledElements = (container: HTMLElement) => {
      const allDivs = container.querySelectorAll('div')

      allDivs.forEach(div => {
        div.style.opacity = ''
        div.style.color = ''
        div.style.backgroundColor = ''

        const style = window.getComputedStyle(div)

        if (parseFloat(style.opacity) === 0.5) {
          div.style.opacity = '0'
          div.style.color = 'rgb(54, 56, 65)'
          div.style.backgroundColor = 'white'
        }
      })
    }
    const calendarObserver = new MutationObserver(() => {
      const curtain = document.querySelector(
        '.--date-range-picker-curtain'
      ) as HTMLElement

      if (curtain) {
        colorizeDisabledElements(curtain)
      }
    })

    if (dateRangePickerRef.current) {
      calendarObserver.observe(dateRangePickerRef.current, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      calendarObserver.disconnect()
    }
  })

  useEffect(() => {
    if (!dateRangePickerRef.current) return

    const handleInputClick = (e: Event) => {
      if (disabled) return

      const target = e.target as HTMLElement
      if (
        target.classList.contains('--input-icon-right') ||
        target.closest('.--input-icon-right')
      ) {
        return
      }

      const displayContainer = (target.closest(
        '.--date-range-picker-display'
      ) || e.currentTarget) as HTMLElement

      const iconButton = displayContainer.querySelector(
        '.--input-icon-right'
      ) as HTMLElement | null

      if (iconButton) {
        iconButton.click()
      }
    }

    const attachInputListeners = () => {
      const container = dateRangePickerRef.current?.querySelector(
        '.--date-range-picker-display'
      )

      if (container) {
        container.removeEventListener('click', handleInputClick)
        container.addEventListener('click', handleInputClick)
      }
    }

    attachInputListeners()

    const inputObserver = new MutationObserver(() => {
      attachInputListeners()
    })

    inputObserver.observe(dateRangePickerRef.current, {
      childList: true,
      subtree: true,
    })

    const observer = new MutationObserver(() => {
      const curtain = dateRangePickerRef.current?.querySelector(
        '.--date-range-picker-curtain'
      ) as HTMLElement
      if (!curtain) return
      if (!onToggleDate && !onButtonClick) return
      if (curtain.querySelector('.custom-checkboxes')) return

      const container = document.createElement('div')
      container.classList.add('custom-checkboxes')
      container.style.display = 'flex'
      container.style.flexDirection = 'column'

      if (onToggleDate) {
        const checkbox1 = document.createElement('input')
        checkbox1.type = 'checkbox'
        checkbox1.id = 'include-weekends'
        checkbox1.checked = isDate ?? false
        checkbox1.addEventListener('change', e => {
          const target = e.target as HTMLInputElement
          onToggleDate(target.checked, 'isDate')
        })
        const label1 = document.createElement('label')
        label1.htmlFor = 'include-weekends'
        label1.textContent = translations.createDate
        label1.prepend(checkbox1)

        const checkbox2 = document.createElement('input')
        checkbox2.type = 'checkbox'
        checkbox2.id = 'include-holidays'
        checkbox2.checked = isDateStart ?? false
        checkbox2.addEventListener('change', e => {
          const target = e.target as HTMLInputElement
          onToggleDate(target.checked, 'isDateStart')
        })
        const label2 = document.createElement('label')
        label2.htmlFor = 'include-holidays'
        label2.textContent = translations.dateStart
        label2.prepend(checkbox2)

        container.appendChild(label1)
        container.appendChild(label2)
        curtain.appendChild(container)
      }

      if (onButtonClick) {
        const containerButton = document.createElement('div')
        containerButton.classList.add('container-button')
        containerButton.style.display = 'flex'
        containerButton.style.flexDirection = 'column'

        const button = document.createElement('button')
        button.classList.add('custom-button')
        button.textContent = translations.filter
        button.addEventListener('click', () => {
          const current = currentValuesRef.current
          onButtonClick({
            value: current.value,
            isDate: current.isDate ?? true,
            isDateStart: current.isDateStart ?? false,
          })
          const iconButton = dateRangePickerRef.current?.querySelector(
            '.--input-icon-right'
          ) as HTMLElement | null
          if (iconButton) iconButton.click()
        })

        containerButton.appendChild(button)
        curtain.appendChild(containerButton)
      }
    })

    observer.observe(dateRangePickerRef.current, {
      childList: true,
      subtree: true,
    })

    return () => {
      observer.disconnect()
      inputObserver.disconnect()
    }
  }, [disabled, isDate, isDateStart, onToggleDate, onButtonClick, translations])

  return (
    <div style={{ position: 'relative' }} ref={dateRangePickerRef}>
      {datePickerProps.dateErrorText && (
        <ErrorText>{datePickerProps.dateErrorText}</ErrorText>
      )}
      {label && <Label>{label}</Label>}
      <StyledDateRangePicker
        width={width}
        value={value as any}
        onChange={handleChange}
        locale={locale}
        dateErrorText={translations.invalidDate}
        background={background}
        focusBorderColor="secondary"
        height="3.875rem"
        noAnimation
        noWeekNums
        $borderColor="gray9"
        displayDateFormat="dd.MM.yyyy"
        disabled={disabled}
        {...datePickerProps}
      />
    </div>
  )
}
